/**
 * /publishers/tracking-domains — operator page for feature #136.
 *
 * Declares a TanStack Router route and renders the
 * `PublisherTrackingDomainsPage` feature. The root wire-up picks this up
 * from `router.tsx`.
 *
 * `?affiliateId=` search param deep-links the page to a specific publisher
 * scope so operators can jump here from an affiliate detail view.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { PublisherTrackingDomainsPage } from "@/features/publisher-tracking-domains/PublisherTrackingDomainsPage";

interface PublishersTrackingDomainsSearch {
	affiliateId?: string;
}

export const publishersTrackingDomainsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/publishers/tracking-domains",
	validateSearch: (
		raw: Record<string, unknown>,
	): PublishersTrackingDomainsSearch => {
		const v = raw.affiliateId;
		if (typeof v === "string" && v.trim().length > 0) {
			return { affiliateId: v.trim() };
		}
		return {};
	},
	component: PublishersTrackingDomainsRoute,
});

export default publishersTrackingDomainsRoute;

export function PublishersTrackingDomainsRoute() {
	const { affiliateId } = publishersTrackingDomainsRoute.useSearch();

	return (
		<div className="flex flex-col">
			<nav
				aria-label="Breadcrumb"
				className="mx-auto w-full max-w-5xl px-6 pt-10 text-sm text-zinc-500 sm:pt-16"
			>
				<ol className="flex flex-wrap items-center gap-1">
					<li>
						<Link
							to="/"
							className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
						>
							Dashboard
						</Link>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li>
						<span className="rounded-[6px] px-1 py-0.5">Publishers</span>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li className="text-zinc-900">Tracking domains</li>
				</ol>
			</nav>

			<PublisherTrackingDomainsPage initialAffiliateId={affiliateId} />
		</div>
	);
}

// Named export for the root wire-up cycle.
export { PublishersTrackingDomainsRoute as PublishersTrackingDomainsPage };
